import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'react-hot-toast';
import { Save, Loader2, ArrowLeft, Truck, Package, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DeliveryCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const soNumber = searchParams.get('so');
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [salesOrders, setSalesOrders] = useState([]);
  const [selectedSO, setSelectedSO] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});

  const [header, setHeader] = useState({
    sales_order_number: soNumber || '',
    planned_goods_issue_date: new Date().toISOString().split('T')[0],
    delivery_note: ''
  });

  useEffect(() => {
    loadSalesOrders();
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (header.sales_order_number) {
      loadSalesOrderDetails(header.sales_order_number);
    }
  // eslint-disable-next-line
  }, [header.sales_order_number]);

  const loadSalesOrders = async () => {
    setIsLoading(true);
    try {
      // Get sales orders that can have deliveries created (OPEN, IN_PROCESS, PARTIALLY_DELIVERED)
      const response = await axios.get(`${BACKEND_URL}/api/otc/sales-orders`);
      const eligibleOrders = (response.data || []).filter(so => 
        ['OPEN', 'IN_PROCESS', 'PARTIALLY_DELIVERED'].includes(so.status)
      );
      setSalesOrders(eligibleOrders);
      
      // If SO number was passed, load it
      if (soNumber) {
        const so = eligibleOrders.find(o => o.sales_order_number === soNumber);
        if (so) {
          setSelectedSO(so);
          initializeSelectedItems(so);
        }
      }
    } catch (error) {
      console.error('Error loading sales orders:', error);
      toast.error('Failed to load sales orders');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSalesOrderDetails = async (soNum) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/otc/sales-orders/${soNum}`);
      setSelectedSO(response.data);
      initializeSelectedItems(response.data);
    } catch (error) {
      console.error('Error loading sales order details:', error);
      toast.error('Failed to load sales order details');
    }
  };

  const initializeSelectedItems = (so) => {
    if (!so || !so.items) return;
    
    const items = {};
    so.items.forEach(item => {
      const remainingQty = item.order_quantity - (item.delivered_quantity || 0);
      if (remainingQty > 0 && !item.is_rejected) {
        items[item.item_number] = {
          selected: true,
          delivery_quantity: remainingQty,
          remaining_quantity: remainingQty
        };
      }
    });
    setSelectedItems(items);
  };

  const handleItemToggle = (itemNumber) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemNumber]: {
        ...prev[itemNumber],
        selected: !prev[itemNumber]?.selected
      }
    }));
  };

  const handleQuantityChange = (itemNumber, quantity) => {
    const maxQty = selectedItems[itemNumber]?.remaining_quantity || 0;
    const validQty = Math.min(Math.max(0, parseFloat(quantity) || 0), maxQty);
    
    setSelectedItems(prev => ({
      ...prev,
      [itemNumber]: {
        ...prev[itemNumber],
        delivery_quantity: validQty
      }
    }));
  };

  const validateForm = () => {
    if (!header.sales_order_number) {
      toast.error('Please select a Sales Order');
      return false;
    }
    if (!header.planned_goods_issue_date) {
      toast.error('Planned Goods Issue Date is required');
      return false;
    }
    
    const hasSelectedItems = Object.values(selectedItems).some(item => item.selected && item.delivery_quantity > 0);
    if (!hasSelectedItems) {
      toast.error('Please select at least one item with quantity > 0');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      // Build delivery items from selected items
      const deliveryItems = [];
      let itemNum = 10;
      
      Object.entries(selectedItems).forEach(([soItemNum, itemData]) => {
        if (itemData.selected && itemData.delivery_quantity > 0) {
          const soItem = selectedSO.items.find(i => i.item_number === parseInt(soItemNum));
          if (soItem) {
            deliveryItems.push({
              item_number: itemNum,
              sales_order_number: header.sales_order_number,
              sales_order_item: parseInt(soItemNum),
              material_id: soItem.material_id,
              item_description: soItem.item_description,
              delivery_quantity: itemData.delivery_quantity,
              unit_of_measure: soItem.unit_of_measure,
              plant: soItem.plant,
              storage_location: soItem.storage_location
            });
            itemNum += 10;
          }
        }
      });

      const deliveryData = {
        sales_order_number: header.sales_order_number,
        ship_to_party: selectedSO.ship_to_party,
        sold_to_party: selectedSO.sold_to_party,
        sales_organization: selectedSO.sales_organization,
        planned_goods_issue_date: header.planned_goods_issue_date,
        delivery_note: header.delivery_note,
        items: deliveryItems
      };

      const response = await axios.post(`${BACKEND_URL}/api/otc/deliveries`, deliveryData);
      toast.success(`Delivery '${response.data.delivery_number}' created successfully!`);
      navigate(`/otc/deliveries/${response.data.delivery_number}`);
    } catch (error) {
      console.error('Error creating delivery:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to create delivery: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/otc/deliveries')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Delivery</h1>
              <p className="text-sm text-gray-600">Create outbound delivery from sales order</p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={isSaving} data-testid="save-delivery-btn">
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Delivery
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Sales Order Selection */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Delivery Header
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sales Order *</Label>
                  <Select 
                    value={header.sales_order_number || '__SELECT__'} 
                    onValueChange={(v) => setHeader({...header, sales_order_number: v === '__SELECT__' ? '' : v})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select Sales Order" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__SELECT__">Select Sales Order</SelectItem>
                      {salesOrders.map(so => (
                        <SelectItem key={so.sales_order_number} value={so.sales_order_number}>
                          {so.sales_order_number} - {so.sold_to_party} ({so.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Planned GI Date *</Label>
                  <Input
                    type="date"
                    value={header.planned_goods_issue_date}
                    onChange={(e) => setHeader({...header, planned_goods_issue_date: e.target.value})}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label>Delivery Note</Label>
                  <textarea
                    value={header.delivery_note}
                    onChange={(e) => setHeader({...header, delivery_note: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                    rows={2}
                    placeholder="Additional notes..."
                    maxLength={500}
                  />
                </div>
              </div>
            </Card>

            {/* Items Selection */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Select Items for Delivery
              </h3>
              
              {!selectedSO ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Select a Sales Order to see available items</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 w-12"></th>
                        <th className="text-left py-2 px-3 text-sm font-semibold">Item</th>
                        <th className="text-left py-2 px-3 text-sm font-semibold">Material</th>
                        <th className="text-left py-2 px-3 text-sm font-semibold">Description</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Order Qty</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Delivered</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Remaining</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Delivery Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSO.items?.map((item) => {
                        const remainingQty = item.order_quantity - (item.delivered_quantity || 0);
                        const isSelectable = remainingQty > 0 && !item.is_rejected;
                        const itemState = selectedItems[item.item_number];
                        
                        return (
                          <tr key={item.item_number} className={`border-b ${!isSelectable ? 'bg-gray-50 opacity-50' : 'hover:bg-gray-50'}`}>
                            <td className="py-2 px-3">
                              {isSelectable && (
                                <Checkbox
                                  checked={itemState?.selected || false}
                                  onCheckedChange={() => handleItemToggle(item.item_number)}
                                />
                              )}
                            </td>
                            <td className="py-2 px-3 font-mono">{item.item_number?.toString().padStart(4, '0')}</td>
                            <td className="py-2 px-3">{item.material_id || '-'}</td>
                            <td className="py-2 px-3">{item.item_description}</td>
                            <td className="py-2 px-3 text-right">{item.order_quantity} {item.unit_of_measure}</td>
                            <td className="py-2 px-3 text-right">{item.delivered_quantity || 0} {item.unit_of_measure}</td>
                            <td className="py-2 px-3 text-right font-medium text-orange-600">{remainingQty} {item.unit_of_measure}</td>
                            <td className="py-2 px-3 text-right w-32">
                              {isSelectable && itemState?.selected && (
                                <Input
                                  type="number"
                                  min="0"
                                  max={remainingQty}
                                  step="0.001"
                                  value={itemState?.delivery_quantity || 0}
                                  onChange={(e) => handleQuantityChange(item.item_number, e.target.value)}
                                  className="w-24 text-right"
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* SO Summary */}
            {selectedSO && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Sales Order Info</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">Sales Order</p>
                    <p className="font-mono font-bold text-orange-600">{selectedSO.sales_order_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Customer</p>
                    <p className="font-medium">{selectedSO.sold_to_party}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Ship-to Party</p>
                    <p className="font-medium">{selectedSO.ship_to_party}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Sales Organization</p>
                    <p className="font-medium">{selectedSO.sales_organization}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {selectedSO.status}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            {/* Delivery Summary */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Delivery Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Selected Items:</span>
                  <span className="font-bold">
                    {Object.values(selectedItems).filter(i => i.selected && i.delivery_quantity > 0).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Quantity:</span>
                  <span className="font-bold">
                    {Object.values(selectedItems)
                      .filter(i => i.selected)
                      .reduce((sum, i) => sum + (i.delivery_quantity || 0), 0)
                      .toFixed(2)}
                  </span>
                </div>
              </div>
            </Card>

            {/* Help */}
            <Card className="p-6 bg-orange-50">
              <h3 className="text-sm font-semibold text-orange-800 mb-2">How it works</h3>
              <ul className="text-xs text-orange-700 space-y-1">
                <li>1. Select a Sales Order</li>
                <li>2. Check items to include in delivery</li>
                <li>3. Adjust delivery quantities if needed</li>
                <li>4. Click "Create Delivery"</li>
                <li>5. Post Goods Issue to update stock</li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DeliveryCreate;
